import { faCircleXmark, faClock, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ImageConfig } from '../ImageConfig'
import { useState, useRef, useEffect } from 'react'

import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import useAuth from '../../hooks/useAuth'
import moment from 'moment'

const OHSupportingDocuments = ({ requestData, setRequestData, isDraftEdit, files, setFiles }) => {
    const { createInfo } = useAuth()

    const axios = useAxiosPrivate()
    const wrapperRef = useRef(null)

    const [supportingDocuments, setSupportingDocuments] = useState([])

    useEffect(() => {
        setSupportingDocuments(requestData?.supporting_documents || [])
    }, [requestData])

    const deleteDocument = async (filename) => {
        try {
            const response = await axios.get(
                `/deactivate-supporting-document/${requestData.id}/${filename}`
            )

            setSupportingDocuments(prevDocs => prevDocs.filter(doc => doc.filename !== filename))
            setRequestData(prevData => ({
                ...prevData,
                supporting_documents: prevData.supporting_documents.filter(doc => doc.filename !== filename)
            }))

            if (response.status === 200) {
                createInfo('success', 'Document deleted successfully')
            }
        } catch (error) {
            console.error('Failed to delete document:', error)
            createInfo('error', 'Failed to delete document')
        }
    }

    const onDragEnter = (e) => {
        e.preventDefault()
        e.stopPropagation()
    }

    const onDragLeave = (e) => {
        e.preventDefault()
        e.stopPropagation()
    }

    const onDragOver = (e) => {
        e.preventDefault()
        e.stopPropagation()
    }

    const onDrop = (e) => {
        e.preventDefault()
        e.stopPropagation()
        const newFiles = Array.from(e.dataTransfer.files)
        handleFiles(newFiles)
    }

    const onFileUpload = (e) => {
        const newFiles = Array.from(e.target.files)
        handleFiles(newFiles)
    }

    const handleFiles = (newFiles) => {
        const validFiles = []
        newFiles.forEach(file => {
            if (file.size <= 10 * 1024 * 1024) {
                validFiles.push(file)
            }
        })
        setFiles({
            ...files,
            fileList: [...files.fileList, ...validFiles]
        })
    }

    const fileRemove = (file) => {
        setFiles({
            ...files,
            fileList: files.fileList.filter(item => item !== file)
        })
    }

    return (
        <div className="mt-6 border border-gray-200 rounded-lg">
            <div
                className="flex items-center justify-between p-2 bg-gray-50 rounded-t-lg"
            >
                <label className="block text-sm font-medium text-gray-700">
                    Supporting Documents (Optional)
                    {isDraftEdit && supportingDocuments && supportingDocuments.length > 0 && (
                        <span className="ml-2 inline-flex items-center pr-2.5 pl-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {supportingDocuments.length} uploaded
                        </span>
                    )}
                    {files.fileList.length > 0 && (
                        <span className="ml-2 inline-flex items-center pr-2.5 pl-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            {files.fileList.length} file{files.fileList.length > 1 ? 's' : ''} pending
                        </span>
                    )}
                </label>
            </div>
            {(files.fileList.length > 0 || (isDraftEdit && supportingDocuments && supportingDocuments.length > 0)) && (
                <div className="px-4 py-2 bg-gray-50 border-t text-sm text-gray-600">
                    {isDraftEdit && supportingDocuments && supportingDocuments.length > 0 &&
                        `${supportingDocuments.length} document${supportingDocuments.length > 1 ? 's' : ''} uploaded`}
                    {isDraftEdit && supportingDocuments && supportingDocuments.length > 0 && files.fileList.length > 0 && ', '}
                    {files.fileList.length > 0 &&
                        `${files.fileList.length} file${files.fileList.length > 1 ? 's' : ''} ready for upload`}
                </div>
            )}
            <div className={`overflow-hidden transition-all duration-700 ease-in-out`}>
                <div className="p-4">
                    {isDraftEdit && supportingDocuments && supportingDocuments.length > 0 && (
                        <div className="mb-6">
                            <h4 className="font-semibold text-gray-900 mb-3 text-sm">Uploaded Documents</h4>
                            <div className="space-y-2">
                                {requestData.supporting_documents.map((doc, index) => (
                                    <div key={index} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                                        <div className="flex items-center">
                                            <img
                                                src={
                                                    ImageConfig[doc.file_name?.split('.').pop()] ||
                                                    ImageConfig['default']
                                                }
                                                alt=""
                                                className="w-6 h-6"
                                            />
                                            <div className="ml-3">
                                                <p className="text-sm font-medium text-gray-900">{doc?.metadata?.original_name}</p>
                                                <p className="text-xs text-gray-500">
                                                    Uploaded {doc.created_at ? moment(doc.created_at).format('DD/MM/YYYY HH:mm') : ''}
                                                    {doc.uploaded_by && ` by ${doc.uploaded_by}`}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={(e) => { e.preventDefault(); deleteDocument(doc.filename); }}
                                                className="inline-flex items-center px-2 py-1 border border-red-300 text-xs leading-4 font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                                title="Delete document"
                                            >
                                                <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mb-4 text-sm">
                        <h3 className="font-semibold text-gray-900 mb-2">
                            Upload Supporting Documents
                        </h3>
                        <p className="text-gray-600 text-xs mb-4">
                            You can upload supporting documents that will help with the assessment. Files will be uploaded when you save or submit the form.
                        </p>
                    </div>

                    <label
                        htmlFor="supporting-file-upload"
                        className="block"
                        ref={wrapperRef}
                        onDragEnter={onDragEnter}
                        onDragLeave={onDragLeave}
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                    >
                        <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-4 py-6">
                            <div className="text-center">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="1.5"
                                    stroke="currentColor"
                                    className="mx-auto h-8 w-8 text-gray-300"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                                    />
                                </svg>
                                <div className="mt-2 flex text-xs leading-6 text-gray-600 justify-center">
                                    <div className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500">
                                        <span>Drag and Drop or click to upload</span>
                                        <input
                                            id="supporting-file-upload"
                                            name="supporting-file-upload"
                                            type="file"
                                            accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.csv,text/csv,.pdf,application/pdf,image/jpeg,image/png,image/tiff"
                                            multiple
                                            className="sr-only"
                                            onChange={onFileUpload}
                                        />
                                    </div>
                                </div>
                                <p className="text-xs leading-5 text-gray-600">
                                    PNG, JPG, CSV, XLS or PDF up to 10MB
                                </p>
                                <p className="error-message">{files?.errorMsg}</p>
                            </div>
                        </div>
                    </label>

                    {files.fileList.length > 0 && (
                        <div className="mt-4 text-xs">
                            <h4 className="font-semibold mb-2 text-yellow-700 flex items-center">
                                <FontAwesomeIcon icon={faClock} className="mr-2 text-sm" />
                                Pending Files ({files.fileList.length})
                            </h4>
                            <div className="border-2 border-dashed border-yellow-300 bg-yellow-50 rounded p-2">
                                {files.fileList.map((item, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-2 mb-2 bg-white border border-yellow-200 rounded shadow-sm"
                                    >
                                        <div className="flex items-center">
                                            <img
                                                src={
                                                    ImageConfig[item.type.split('/')[1]] ||
                                                    ImageConfig['default']
                                                }
                                                alt=""
                                                className="w-6 h-6"
                                            />
                                            <div className="ml-2">
                                                <p className="break-words text-gray-700 font-medium">{item.name}</p>
                                                <p className="text-xs text-gray-500">Ready to upload</p>
                                            </div>
                                        </div>
                                        <span
                                            className="hover:text-red-700 cursor-pointer"
                                            onClick={() => fileRemove(item)}
                                        >
                                            <FontAwesomeIcon
                                                icon={faCircleXmark}
                                                className="text-gray-700 hover:text-red-700 w-4 h-4"
                                            />
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default OHSupportingDocuments